/* libs */
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import firebase from "firebase";
import { isEmpty } from "lodash";

/* components */
import Page from "../../shared/Page";
import Switcher from "../../ui/Switcher";

/* helpers */
import * as appConfigActions from "../../../actions/appConfigActions";
import * as controlPanelActions from "../../../actions/controlPanelActions";
import * as globalActions from "../../../actions/globalActions";
import * as sessionsActions from "../../../actions/sessionsActions";
import parseAppKey from "../../../helpers/routeHelpers/parseAppKey";
import Chat from "../../ui/Chat";
import styled, { ThemeProvider } from "styled-components";
import { style } from "../../ui/Chat/styles";
import FirebaseAuth from "../../shared/FirebaseAuth/FirebaseAuth";
import { Link } from "react-router-dom";
import isLightOrDark from "../../ui/Chat/services/isLightOrDark";

const COLLECTION = "questions";

const switcherOptions = [{ label: "Recent", value: "recent" }, { label: "Popular", value: "popular" }];

class ChatManagerPage extends Component {
	constructor(props) {
		super(props);
		this.appKey = this.props.match.params.appKey;
		this.state = {
			data: {},
			popularData: {},
			filter: "recent",
			projectFirebase: null,
		};
	}

	componentDidMount() {
		const {
			apps,
			sessions: { sessions },
			appConfig: { appConfig },
			appConfigActions,
			sessionsActions,
		} = this.props;

		if (isEmpty(appConfig)) {
			appConfigActions.loadAppConfig(this.appKey);
		}
		sessionsActions.loadSessions(this.appKey);

		let appId = apps ? apps.find(app => app.key === parseAppKey()).id : null;
		if (sessions.length && sessions[0].appId === appId) {
			this.subscribeToSessions();
		}
	}

	componentDidUpdate(prevProps) {
		const {
			sessions: { sessions: prevSessions },
		} = prevProps;
		const {
			sessions: { sessions: currentSessions },
		} = this.props;

		if (prevSessions.length === 0 && currentSessions.length) {
			this.subscribeToSessions();
		}
	}

	subscribeToSessions = () => {
		const {
			appConfig: { appConfig, appName },
			sessions: { sessions },
		} = this.props;
		if (!appConfig.webConfig) {
			console.error("Web Config is missing");
		} else {
			const { projectFirebase: firebaseFromState } = this.state;
			let projectFirebase = null;
			if (!firebaseFromState) {
				const initializedAppApiKeys = firebase.apps.map(a => a["options_"].apiKey);
				const webConfig = JSON.parse(appConfig.webConfig);
				const { apiKey: apiKeyFromConfig } = webConfig;

				// if an existing firebase instance has the same API key, we will use that.
				// if that doesn't exist yet, we need to create a new one

				if (initializedAppApiKeys.includes(apiKeyFromConfig)) {
					projectFirebase = firebase.apps.find(a => a["options_"].apiKey === apiKeyFromConfig);
				} else {
					// we need to check and see if any other firebase instances of this app have been created yet.
					// if we have others, we need to auto-increment the firebase instance name so we don't get an
					//   error about the app already being initialized
					const allFirebaseNames = firebase.apps.map(a => a["name_"]).filter(a => a !== "[DEFAULT]");
					if (isEmpty(allFirebaseNames)) {
						projectFirebase = firebase.initializeApp(webConfig, `${appName}-firebase1`);
					} else {
						const newestInstance = allFirebaseNames
							.filter(a => a.includes(appName))
							.sort()
							.splice(-1)
							.pop();
						const newestId = parseInt(newestInstance.replace(/(\S+)(-firebase(\d+))/g, "$3"));
						const newId = newestId + 1;
						projectFirebase = firebase.initializeApp(webConfig, `${appName}-firebase${newId}`);
					}
				}
			} else {
				projectFirebase = firebaseFromState;
			}
			this.setState({ projectFirebase });

			const db = projectFirebase.firestore();
			// Only set settings if Firestore hasn't been used yet
			try {
				db.settings({ timestampsInSnapshots: true });
			} catch (error) {
				// Settings already configured, ignore the error
				console.log("Firestore settings already configured");
			}

			sessions.forEach(session => {
				const queryNew = db
					.collection(COLLECTION)
					.where("sessionId", "==", session.id)
					.orderBy("flags")
					.orderBy("timestamp", "desc");

				const queryPopular = db
					.collection(COLLECTION)
					.where("sessionId", "==", session.id)
					.orderBy("flags")
					.orderBy("votes", "desc");

				queryNew.onSnapshot(querySnapshot => {
					let questions = [];
					querySnapshot.forEach(doc => {
						questions.push(Object.assign({}, doc.data(), { docId: doc.ref.id }));
					});
					if (questions.length) {
						this.setState({ data: Object.assign({}, this.state.data, { [session.id]: questions }) });
					}
				});

				queryPopular.onSnapshot(querySnapshot => {
					let questions = [];
					querySnapshot.forEach(doc => {
						questions.push(Object.assign({}, doc.data(), { docId: doc.ref.id }));
					});
					if (questions.length) {
						this.setState({
							popularData: Object.assign({}, this.state.popularData, { [session.id]: questions }),
						});
					}
				});
			});
		}
	};

	getAppId = key => {
		const { apps } = this.props;
		const app = apps.find(a => a.key === key);
		return app.id;
	};

	loadSessions = appId => {
		this.props.sessionsActions.loadSessions(appId);
	};

	banQuestion = docId => {
		const { projectFirebase } = this.state;
		if (window.confirm("Are you sure you want to remove this question?")) {
			const db = projectFirebase.firestore();
			db.collection(COLLECTION)
				.doc(docId)
				.update({ flags: 100 });
		}
	};
	restoreQuestion = docId => {
		const { projectFirebase } = this.state;
		if (window.confirm("Are you sure you want to restore this question?")) {
			const db = projectFirebase.firestore();
			db.collection(COLLECTION)
				.doc(docId)
				.update({ flags: 0 });
		}
	};

	changeFilter = (index, option) => {
		this.setState({ filter: option.value });
	};

	render() {
		const { sessions } = this.props.sessions;
		const { chatPages } = this.props.pages;
		const data = this.state.filter === "recent" ? this.state.data : this.state.popularData;

		const chatPageId = Object.keys(chatPages).find(
			page => !!chatPages[page].properties.channels.find(channel => channel.id === this.props.match.params.chatId)
		);
		const chatPage = Object.values(chatPages).find(
			page => !!page.properties.channels.find(channel => channel.id === this.props.match.params.chatId)
		);
		const channel = chatPage?.properties.channels.find(channel => channel.id === this.props.match.params.chatId);
		const shade = isLightOrDark(channel?.color ?? "#FFFFFF");
		const theme = {
			textColor: shade === "light" ? "#001000" : "#FFFFFF",
			buttonTextColor: shade === "light" ? "#FFFFFF" : "#000000",
			buttonBackgroundColor: shade === "light" ? "#000000" : "#FFFFFF",
			buttonHoverBackgroundColor: "#102010",
			backgroundColor: "#FFFFFF",
			backgroundImage: "#FFFFFF",
			wellTextColor: shade === "light" ? "#001000" : "#FFFFFF",
			wellBackgroundColor: channel ? channel.color : "#FFFFFF",
			navBackgroundColor: "#FFFFFF",
			navLinkColor: "#001000",
			navLinkHoverColor: "#102010",
		};

		const chatChannels = [];

		Object.keys(chatPages).forEach(chatPageId => {
			chatPages[chatPageId].properties.channels.forEach(channel => {
				if (!chatChannels.find(c => c.id === channel.id)) {
					chatChannels.push({ id: channel.id, name: channel.name, image: channel.uri, color: channel.color });
				}
			});
		});

		if (!this.state.projectFirebase) {
			return null;
		}

		return (
			<FirebaseAuth firebaseApp={this.state.projectFirebase}>
				<Page title={this.props.match.params.chatId}>
					<div className="chat-tabs">
						{chatChannels.map(chatChannel => (
							<Link to={`./${chatChannel.id}`}>
								<div
									key={chatChannel.id}
									className={`chat-tab ${
										this.props.match.params.chatId === chatChannel.id ? "active" : ""
									}`}
									style={{
										backgroundColor:
											this.props.match.params.chatId === chatChannel.id
												? channel.color
												: "#dbdee1",
									}}
								>
									<div
										className="image"
										style={{ backgroundImage: `url('${chatChannel.image}')` }}
									></div>
									<small
										style={{
											color:
												this.props.match.params.chatId === chatChannel.id && shade === "dark"
													? "#FFFFFF"
													: "#24272b",
										}}
									>
										{chatChannel.name}
									</small>
								</div>
							</Link>
						))}
					</div>

					<ThemeProvider theme={theme}>
						<style.ChatContainer>
							<Chat
								clientId={this.appKey}
								id={this.props.match.params.chatId}
								isModerator={true}
								enableAnonymous={false}
								isLightBackground
								chatClosed={false}
								isLoggingOut={false}
								contentId={this.props.match.params.chatId}
								useChatProcessor
								canUseChatProcessor
								enablePreview
								chatBlacklist={[]}
								firebaseApp={this.state.projectFirebase}
								// 	onMessages={onMessages}
								// 	setCurrentChat={setCurrentChat}
								experienceId={this.props.match.params.expId}
								pageId={chatPageId}
								channelName={channel?.name}
							/>
						</style.ChatContainer>
					</ThemeProvider>
					<div
						className="btn m-top btn-block"
						onClick={() => {
							this.state.projectFirebase.auth().signOut();
						}}
					>
						Switch Chat User
					</div>
				</Page>
			</FirebaseAuth>
		);
	}
}

ChatManagerPage.propTypes = {
	actions: PropTypes.any,
	appConfig: PropTypes.object,
	appConfigActions: PropTypes.object,
	apps: PropTypes.array,
	controlPanelActions: PropTypes.object,
	globalActions: PropTypes.any,
	history: PropTypes.object,
	match: PropTypes.any,
	pagesActions: PropTypes.object,
	sessions: PropTypes.object,
	sessionsActions: PropTypes.object,
	topics: PropTypes.array,
	topicsActions: PropTypes.object,
};

const mapStateToProps = state => ({
	appConfig: state.appConfig,
	apps: state.apps.apps,
	sessions: state.sessions,
	topics: state.topics.topics,
	pages: state.pages,
});

const mapDispatchToProps = dispatch => ({
	appConfigActions: bindActionCreators(appConfigActions, dispatch),
	controlPanelActions: bindActionCreators(controlPanelActions, dispatch),
	globalActions: bindActionCreators(globalActions, dispatch),
	sessionsActions: bindActionCreators(sessionsActions, dispatch),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ChatManagerPage);
